// The once-a-month thank-you at the top of the chats (M55,
// docs/run-by-families.md D197).

import type { MessageKey, Vars } from '../i18n';
import type { CostSummary } from './api';
import { formatMoney } from '../content/site';

const SEEN_KEY = 'twinden:thanksSeenMonth';

export function thanksSeenMonth(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

export function markThanksSeen(month: string) {
  try {
    localStorage.setItem(SEEN_KEY, month);
  } catch {
    // Private mode: the card goes for this visit only.
  }
}

/** Something to thank families for: money in, or a count of families. */
export function hasFigures(costs: CostSummary): boolean {
  return costs.received > 0 || (costs.familiesCount ?? 0) > 0;
}

/**
 * Whether the card shows (D197): a published month not yet seen on this
 * device, unless there's neither a Chip in link nor anything to report.
 */
export function showThanks(input: {
  costs: CostSummary | null | undefined;
  supportUrl: string | null;
  seenMonth: string | null;
}): boolean {
  const { costs, supportUrl, seenMonth } = input;
  if (!costs) return false;
  if (seenMonth === costs.month) return false;
  return !!supportUrl || hasFigures(costs);
}

/** The card's sentence: its catalog key and the holes to fill (D197). */
export function thanksText(
  costs: CostSummary,
  month: string
): { key: MessageKey; vars: Vars } {
  const vars: Vars = {
    month,
    received: formatMoney(costs.received),
    total: formatMoney(costs.total),
  };
  if (!hasFigures(costs)) return { key: 'chat.thanks.noFigures', vars };
  const covered = costs.received >= costs.total;
  // A count of 0 or no count at all: leave the number out.
  const count = costs.familiesCount ?? 0;
  if (count === 0) {
    return { key: covered ? 'chat.thanks.covered' : 'chat.thanks.short', vars };
  }
  const key: MessageKey = covered
    ? count === 1
      ? 'chat.thanks.coveredOne'
      : 'chat.thanks.coveredMany'
    : count === 1
      ? 'chat.thanks.shortOne'
      : 'chat.thanks.shortMany';
  return { key, vars: { ...vars, count } };
}

/** "2026-10" → "October" (or "अक्तूबर"), the card's first word. */
export function monthOnly(month: string, locale: string): string {
  const [year, index] = month.split('-').map(Number) as [number, number];
  return new Date(year, index - 1, 1).toLocaleDateString(locale, {
    month: 'long',
  });
}
